import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectBusca } from "@/components/padroes";
import { api } from "@/lib/api";
import { centsToInput, formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import { beneficiosDoVinculo, type BeneficioCatalogo, type FolhaBeneficioDetalhe } from "@/data/beneficios";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  catalogo: BeneficioCatalogo[];
  usuarios: User[];
  onSalvo: () => void;
}

export const LancarBeneficioModal = ({ open, onClose, folhaId, catalogo, usuarios, onSalvo }: Props) => {
  const [colaboradorId, setColaboradorId] = useState("");
  const [tipo, setTipo] = useState("");
  const [valor, setValor] = useState("");
  const [manter, setManter] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) return;
    setColaboradorId("");
    setTipo("");
    setValor("");
    setManter(false);
    setLancados(null);
  }, [open]);

  // O que o colaborador escolhido já tem nesta folha — a tela só carrega uma
  // página das linhas, então consulta a linha dele na API.
  const [lancados, setLancados] = useState<{ colaboradorId: string; valores: Record<string, number> } | null>(null);
  useEffect(() => {
    if (!open || !colaboradorId) return;
    let ativo = true;
    api
      .get<FolhaBeneficioDetalhe>(`/beneficios/folhas/${folhaId}?colaboradorId=${encodeURIComponent(colaboradorId)}`)
      .then((f) => ativo && setLancados({ colaboradorId, valores: f.linhas[0]?.valores ?? {} }))
      .catch(() => ativo && toast.error("Erro ao consultar o que já foi lançado para o colaborador"));
    return () => {
      ativo = false;
    };
  }, [open, folhaId, colaboradorId]);

  // Só os benefícios que valem para o vínculo do colaborador escolhido.
  const vinculo = usuarios.find((u) => u.id === colaboradorId)?.vinculo ?? "pj";
  const opcoes = beneficiosDoVinculo(catalogo, vinculo);
  const doSistema = !!opcoes.find((b) => b.tipo === tipo)?.sistema;
  useEffect(() => {
    if (tipo && !beneficiosDoVinculo(catalogo, vinculo).some((b) => b.tipo === tipo)) setTipo("");
  }, [catalogo, vinculo, tipo]);

  // Mostra o que já existe para o RH saber que vai substituir, não somar.
  const atual = lancados?.colaboradorId === colaboradorId ? (lancados.valores[tipo] ?? 0) : 0;

  useEffect(() => {
    if (colaboradorId && tipo) setValor(atual > 0 ? centsToInput(atual) : "");
  }, [colaboradorId, tipo, atual]);

  const salvar = async () => {
    if (!colaboradorId || !tipo) {
      toast.error("Selecione o colaborador e o benefício");
      return;
    }
    let reais: number;
    try {
      reais = parseReaisInput(valor);
    } catch {
      toast.error("Valor inválido");
      return;
    }
    if (reais < 0) {
      toast.error("O valor não pode ser negativo");
      return;
    }

    setSalvando(true);
    try {
      await api.post(`/beneficios/folhas/${folhaId}/lancamentos`, {
        colaboradorId,
        tipo,
        valor: reais,
        manterNosProximos: manter && !doSistema,
      });
      toast.success(
        (reais === 0 ? "Benefício removido" : "Benefício lançado") +
          (manter && !doSistema ? " e mantido nos próximos meses" : ""),
      );
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao lançar benefício");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lançar benefício</DialogTitle>
          <DialogDescription>
            Define quanto o colaborador recebe deste benefício nesta folha. Não entra no cálculo da
            folha de pagamento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Colaborador</Label>
            <SelectBusca
              value={colaboradorId}
              onChange={setColaboradorId}
              placeholder="Selecione o colaborador"
              placeholderBusca="Buscar por nome ou e-mail..."
              vazio="Nenhum colaborador encontrado."
              opcoes={usuarios.map((u) => ({ value: u.id, label: u.name, detalhe: u.email }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Benefício</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o benefício" />
              </SelectTrigger>
              <SelectContent>
                {opcoes.map((b) => (
                  <SelectItem key={b.tipo} value={b.tipo}>
                    {b.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lancar-valor">Valor (R$)</Label>
            <Input
              id="lancar-valor"
              value={valor}
              onChange={(e) => setValor(maskReaisInput(e.target.value))}
              inputMode="numeric"
              placeholder="0,00"
            />
            {atual > 0 && (
              <p className="text-xs text-muted-foreground">
                Já lançado nesta folha: {formatCents(atual)}. O novo valor substitui esse; zero remove.
              </p>
            )}
          </div>

          {doSistema ? (
            <p className="rounded-md border p-3 text-xs text-muted-foreground">
              O complemento de salário vem do cadastro do colaborador e entra sozinho em cada folha de
              benefícios. O valor lançado aqui vale só para esta folha.
            </p>
          ) : (
          <div className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              id="lancar-manter"
              checked={manter}
              onCheckedChange={(v) => setManter(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="lancar-manter" className="font-normal">
              Manter este valor nos próximos meses
              <span className="block text-xs text-muted-foreground">
                Vira o valor do colaborador nas próximas folhas de benefícios (Usuários → Benefícios). Igual ao fixo, ele volta a seguir o fixo.
              </span>
            </Label>
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
